import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import { classnames, TArg } from 'tailwindcss-classnames';
import ImageCaption from 'components/helpers/ImageCaption/ImageCaption';
import Heading, { propsFromFields } from 'components/helpers/Heading';
import Button from 'components/helpers/Button';
import { ButtonVariant } from 'components/helpers/Button/WithButton';
import { getEnumValue } from 'lib/util/enum';
import { usePageSectionTheme } from 'components/globalContext/PageSectionThemeContext';

export type ImageBlockProps = Sitecore.Override.ComponentBase &
  Components.General.ImageBlock.Fields.ImageBlock;

type ImageAlignmentOptions = 'left' | 'center' | 'right';

const ImageBlock = ({ fields }: ImageBlockProps): JSX.Element => {
  const { theme } = usePageSectionTheme();

  // Fail out if we don't have any fields
  if (!fields) {
    return <></>;
  }

  const imageAlignment = fields.imageAlignment
    ? getEnumValue<ImageAlignmentOptions>(fields.imageAlignment)
    : undefined;

  const headingProps = propsFromFields(fields);
  const hasContent = !!headingProps.headingText?.value || !!fields.cta1?.value?.href;

  // Only apply link-secondary variant for navy-blue and night themes
  const themesWithSecondaryLinks = ['theme-navy-blue', 'theme-night'];
  const canUseSecondaryLink = themesWithSecondaryLinks.includes(theme);
  const ctaVariant: ButtonVariant =
    fields.ctaSecondaryColor?.value && canUseSecondaryLink ? 'link-secondary' : 'link';
  const ctaHasArrow = !!fields.ctaHasArrow?.value;
  const ctaDisabled = !!fields.cta1Disable?.value;
  const applyCircleFilter = !!fields.applyCircleFilter?.value;
  const circleFilterClasses = applyCircleFilter
    ? classnames('rounded-full', 'overflow-hidden', 'aspect-square', 'image-block-rounded')
    : undefined;

  // Build component class list
  const isInline = imageAlignment === 'left' || imageAlignment === 'right';
  const hasImageOrCaption = !!fields.featuredImage?.value?.src || !!fields.imageCaption?.value;

  // Heading top margin: only if content above, skip on mobile if inline
  const getHeadingTopMargin = (): TArg => {
    if (!hasImageOrCaption) {
      return undefined;
    }
    return isInline ? 'lg:mt-4' : 'mt-4';
  };
  const headingTopMargin = getHeadingTopMargin();

  // Image wrapper — fixed icon size when alignment is set, full-width otherwise
  const imageWrapperClasses = classnames(
    'relative',
    'w-[55px]' as TArg,
    'md:w-[85px]' as TArg,
    'shrink-0',
    circleFilterClasses,
    imageAlignment === 'right' ? 'lg:ml-auto' : undefined,
    imageAlignment === 'center' ? 'mx-auto' : undefined
  );

  const noAlignImageWrapperClasses = classnames('relative', circleFilterClasses);

  const alignedWrapperClasses = classnames({
    'max-lg:flex': isInline,
    'max-lg:items-start': isInline,
    'max-lg:gap-4': isInline,
    'max-lg:flex-row-reverse': imageAlignment === 'right',
  } as TArg);

  const imageBlockContent = hasContent ? (
    <div
      className={classnames({
        'text-center': headingProps.headingAlignment === 'center',
        'text-right': headingProps.headingAlignment === 'right',
      })}
    >
      <Heading {...headingProps} className={classnames('mb-2', headingTopMargin)} />
      <Button
        linkField={fields.cta1}
        variant={ctaVariant}
        customIcon={ctaHasArrow ? 'arrow-right' : false}
        disabled={ctaDisabled}
        className="mt-4 underline"
      />
    </div>
  ) : null;

  const innerContent = (
    <>
      <div
        className={imageAlignment === undefined ? noAlignImageWrapperClasses : imageWrapperClasses}
      >
        <ImageWrapper
          field={fields.featuredImage}
          layout="responsive"
          className={applyCircleFilter ? classnames('object-cover') : undefined}
        />
      </div>
      <ImageCaption
        imageCaption={fields.imageCaption}
        classNames={classnames('px-4', {
          'text-center': headingProps.headingAlignment === 'center',
          'text-right': headingProps.headingAlignment === 'right',
        })}
      />
      {imageBlockContent}
    </>
  );

  return (
    <div
      data-component="authorable/general/image-block"
      data-alignment={imageAlignment}
      className="image-block"
    >
      {imageAlignment === undefined ? (
        innerContent
      ) : (
        <div className={alignedWrapperClasses}>{innerContent}</div>
      )}
    </div>
  );
};

export default ImageBlock;
